// Logique d'accès partagée (client + serveur) — brique S3 « blocage fin d'essai ».
//
// SOURCE DE VÉRITÉ UNIQUE de la décision « cette utilisatrice a-t-elle un accès actif ? ».
// Réutilisée par le mur serveur (server/middleware/acces.ts) ET le mur front (src/App.tsx)
// pour éviter toute divergence. Fonction PURE (aucune dépendance ; `maintenant` injectable
// pour les tests).
//
// Règle (cf. webhook S4d : à la résiliation, le `tier` n'est PAS rétrogradé — l'accès réel se
// décide ici, sur le statut Stripe + la fin de période payée) :
//   • admin                              → toujours actif (Thérèse / support) ;
//   • abonnement Stripe (statut présent) :
//       - 'active' ou 'trialing'         → actif ;
//       - sinon (canceled, past_due, unpaid, incomplete…) → actif tant que la période déjà
//         payée n'est pas écoulée (currentPeriodEnd dans le futur) ;
//   • sans abonnement Stripe :
//       - tier payant (starter/premium) = compte OFFERT (S2 : à vie / 1 an) → actif, borné par
//         currentPeriodEnd s'il est renseigné, sinon illimité ;
//       - tier 'trial'                   → actif tant que trialEndsAt est dans le futur.

// Forme minimale d'utilisateur nécessaire à la décision. Les dates arrivent en texte ISO côté
// client (JSON) ou en objet Date côté serveur (Drizzle) : on accepte les deux.
export interface UtilisateurAcces {
  role: 'user' | 'admin'
  tier: 'trial' | 'starter' | 'premium'
  trialEndsAt: string | Date | null
  subscriptionStatus: string | null
  currentPeriodEnd: string | Date | null
}

// Statuts Stripe considérés comme « accès en cours » sans condition de date.
const STATUTS_ACTIFS: readonly string[] = ['active', 'trialing']

// Vrai si `date` (texte ISO ou Date) est strictement postérieure à `maintenant`.
function estFutur(date: string | Date | null, maintenant: Date): boolean {
  if (!date) return false
  const d = date instanceof Date ? date : new Date(date)
  return d.getTime() > maintenant.getTime()
}

/** Décide si l'utilisatrice a un accès actif (création / modifications autorisées). */
export function accesActif(user: UtilisateurAcces, maintenant: Date = new Date()): boolean {
  if (user.role === 'admin') return true

  // Abonnement Stripe géré : le statut prime, avec tolérance jusqu'à la fin de période payée.
  if (user.subscriptionStatus) {
    if (STATUTS_ACTIFS.includes(user.subscriptionStatus)) return true
    return estFutur(user.currentPeriodEnd, maintenant)
  }

  // Pas d'abonnement Stripe : comptes offerts (tier payant manuel) ou essai.
  if (user.tier === 'starter' || user.tier === 'premium') {
    // Compte offert : actif tant qu'aucune date de fin n'est dépassée (sinon illimité).
    return user.currentPeriodEnd ? estFutur(user.currentPeriodEnd, maintenant) : true
  }

  // tier 'trial' : dépend de la fin d'essai.
  return estFutur(user.trialEndsAt, maintenant)
}

// Jours pleins restants jusqu'à `date` (arrondi au supérieur), 0 si null ou déjà passée.
function joursRestants(date: string | Date | null, maintenant: Date): number {
  if (!date) return 0
  const d = date instanceof Date ? date : new Date(date)
  const ms = d.getTime() - maintenant.getTime()
  if (ms <= 0) return 0
  return Math.ceil(ms / (24 * 60 * 60 * 1000))
}

/** Libellé court de la formule d'un compte (affichage admin). */
export function libelleFormule(tier: 'trial' | 'starter' | 'premium'): string {
  if (tier === 'starter') return 'Starter'
  if (tier === 'premium') return 'Premium'
  return 'Essai gratuit'
}

/**
 * Libellé lisible du statut d'un compte (écran admin « Comptes clientes »).
 * Sans date formatée (l'échéance est affichée dans une colonne dédiée) pour rester pur
 * et indépendant de la locale.
 */
export function libelleStatutCompte(user: UtilisateurAcces, maintenant: Date = new Date()): string {
  if (user.role === 'admin') return 'Admin'

  // Abonnement Stripe géré.
  if (user.subscriptionStatus) {
    if (STATUTS_ACTIFS.includes(user.subscriptionStatus)) return 'Abonnée'
    return estFutur(user.currentPeriodEnd, maintenant) ? 'Résiliée' : 'Accès expiré'
  }

  // Comptes offerts (tier payant sans abonnement Stripe).
  if (user.tier === 'starter' || user.tier === 'premium') {
    if (user.currentPeriodEnd && !estFutur(user.currentPeriodEnd, maintenant)) return 'Accès expiré'
    return 'Offert'
  }

  // Essai.
  const j = joursRestants(user.trialEndsAt, maintenant)
  return j > 0 ? `Essai (${j} j)` : 'Essai expiré'
}
